import {
  parseCanvasAllData,
  isPageSkeleton,
} from "@/utils/canvasPageLoader";
import { normalizeCanvasAllDataString } from "@/utils/canvasInitData";
import { pruneCanvasErrorMap } from "@/utils/canvasValidation";

const STORE_MUTATIONS = {
  editMode: "setEditMode",
  canvasStyleData: "setCanvasStyle",
  projectStyleData: "setProjectStyle",
  pageOptions: "setPageOptions",
  componentData: "setComponentData",
  canvasAllData: "setCanvasAllData",
  canvasLazyLoad: "setCanvasLazyLoad",
  canvasLoadedPageIds: "setCanvasLoadedPageIds",
  canvasServerButtonCount: "setCanvasServerButtonCount",
  projectFileResouces: "setProjectFileResouces",
  selectedCurrentPage: "setSelectedCurrentPage",
  componentDataPageId: "setComponentDataPageId",
};

export function snapshotPreviewStore(store) {
  const snap = { session: {} };
  Object.keys(STORE_MUTATIONS).forEach((k) => {
    const v = store.state[k];
    snap[k] =
      v != null && typeof v === "object"
        ? JSON.parse(JSON.stringify(v))
        : v;
  });
  snap.session.pageSortData = sessionStorage.getItem("pageSortData");
  snap.session.interactionPackageData = sessionStorage.getItem("interactionPackageData");
  snap.session.canvasStyle = sessionStorage.getItem("canvasStyle");
  snap.session.canvasErrorMap = sessionStorage.getItem("canvasErrorMap");
  return snap;
}

export function restorePreviewStore(store, snap) {
  if (!snap) {
    return;
  }
  Object.keys(STORE_MUTATIONS).forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(snap, k)) {
      store.commit(STORE_MUTATIONS[k], snap[k]);
    }
  });
  store.commit("setCurErrorMap");
  const s = snap.session || {};
  if (s.pageSortData != null) {
    sessionStorage.setItem("pageSortData", s.pageSortData);
  }
  if (s.interactionPackageData != null) {
    sessionStorage.setItem("interactionPackageData", s.interactionPackageData);
  }
  if (s.canvasStyle != null) {
    sessionStorage.setItem("canvasStyle", s.canvasStyle);
  }
  if (s.canvasErrorMap != null) {
    sessionStorage.setItem("canvasErrorMap", s.canvasErrorMap);
  }
}

function pickCanvasPageArray(canvasData, pageKey) {
  if (!canvasData || pageKey === undefined || pageKey === null) {
    return null;
  }
  const s = String(pageKey);
  if (Object.prototype.hasOwnProperty.call(canvasData, s)) {
    return canvasData[s];
  }
  for (const k of Object.keys(canvasData)) {
    if (String(k) === s) {
      return canvasData[k];
    }
  }
  return null;
}

export function buildPageOptionsFromCanvas(canvasAllData, pageSort) {
  const data = parseCanvasAllData(canvasAllData);
  const arr = [];
  const childMap = {};
  Object.keys(data).forEach((p) => {
    const pageArr = data[p];
    if (!Array.isArray(pageArr) || !pageArr[0]) {
      return;
    }
    const back = pageArr[0];
    const pageName = (back.style && back.style.pageName) || `页面${p}`;
    const item = { label: pageName, value: p };
    arr.push(item);
    if (data[p][0].isChildPage) {
      const parentId = data[p][0].parentId;
      const parentKey = parentId != null ? String(parentId) : "";
      if (parentKey) {
        if (!childMap[parentKey]) {
          childMap[parentKey] = [];
        }
        childMap[parentKey].push(item);
      }
    }
  });
  let sortdata = pageSort && pageSort.length ? pageSort : [1];
  const sortedArr = sortdata.reduce((res, page) => {
    const arrItem = arr.find((item) => String(item.value) === String(page));
    if (arrItem) {
      const childKey = String(page);
      if (childMap[childKey]) {
        arrItem.children = childMap[childKey];
      }
      res.push(arrItem);
    }
    return res;
  }, []);
  return sortedArr;
}

/**
 * 将 findByProjectId 结果写入 Vuex，供 Editor/Preview 使用（不读本地草稿）。
 */
export function bootstrapProjectForPreview(store, projectData) {
  const {
    panel_data,
    project_height,
    project_width,
    page_sort,
    interaction_package_data,
    lazy_load,
    button_count,
    file_resouces,
  } = projectData || {};

  let pageSort = page_sort;
  if (typeof pageSort === "string") {
    try {
      pageSort = JSON.parse(pageSort);
    } catch (e) {
      pageSort = ["1"];
    }
  }
  if (!Array.isArray(pageSort) || !pageSort.length) {
    pageSort = ["1"];
  }
  sessionStorage.setItem("pageSortData", JSON.stringify(pageSort));

  let interactionPackageData = [];
  try {
    interactionPackageData = interaction_package_data
      ? JSON.parse(interaction_package_data)
      : [];
  } catch (e) {
    interactionPackageData = [];
  }
  sessionStorage.setItem("interactionPackageData", JSON.stringify(interactionPackageData));

  const useLazy = !!lazy_load;
  store.commit("setCanvasLazyLoad", useLazy);
  store.commit("setCanvasServerButtonCount", button_count || 0);
  store.commit("setProjectFileResouces", file_resouces || []);

  const loadedMap = {};
  const normalized = normalizeCanvasAllDataString(panel_data);
  const parsedPanel = parseCanvasAllData(normalized);
  if (useLazy) {
    const firstId = String(pageSort[0] || "1");
    const firstArr = pickCanvasPageArray(parsedPanel, firstId);
    if (firstArr && !isPageSkeleton(firstArr)) {
      loadedMap[firstId] = true;
    } else if (firstArr && firstArr.length > 1) {
      loadedMap[firstId] = true;
    }
  } else if (panel_data) {
    Object.keys(parsedPanel).forEach((pid) => {
      loadedMap[String(pid)] = true;
    });
  }
  store.commit("setCanvasLoadedPageIds", loadedMap);

  const firstPage = String(pageSort[0] || "1");
  sessionStorage.setItem(
    "canvasStyle",
    JSON.stringify({
      height: Number(project_height),
      width: Number(project_width),
      scale: 100,
      [firstPage]: {
        height: Number(project_height),
        width: Number(project_width),
        scale: 100,
      },
    })
  );

  store.commit("setProjectStyle", {
    height: Number(project_height),
    width: Number(project_width),
    scale: 100,
  });

  store.commit("setCanvasAllData", normalized);

  const pageOptions = buildPageOptionsFromCanvas(normalized, pageSort);
  store.commit("setPageOptions", pageOptions);
  pruneCanvasErrorMap(parsedPanel);

  const errMap = {};
  pageSort.forEach((id) => {
    errMap[id] = {};
  });
  sessionStorage.setItem("canvasErrorMap", JSON.stringify(errMap));

  const pageKey = pageOptions[0] != null ? String(pageOptions[0].value) : firstPage;
  store.commit("setSelectedCurrentPage", pageKey);
  store.commit("setComponentDataPageId", pageKey);
  const pageArr = pickCanvasPageArray(parsedPanel, pageKey) || [];
  store.commit("setComponentData", pageArr);
  store.commit("setEditMode", "preview");
}
